import { getCachedUser } from '@/api/client';
import { itemsForUser, userCacheKey, type QueuedActionType } from '@/lib/offlineReplay';

const DB_NAME = 'dispatchdash-offline';
const DB_VERSION = 4;
const QUEUE_STORE = 'event-queue';
export const OFFLINE_QUEUE_CHANGED = 'dispatchdash:offline-queue-changed';
const MANIFEST_STORE = 'manifest-cache';
const TRIP_STORE = 'crew-trip-cache';
const ATTENDANCE_STORE = 'crew-attendance-cache';

export interface QueuedEvent {
  id: string;
  type: QueuedActionType;
  payload: Record<string, unknown>;
  createdAt: string;
  retries: number;
  /** Signed-in user who queued it. Only that user's session sends it (legacy items: anyone). */
  userId?: string;
  /** Idempotency key (Idempotency-Key header + body clientEventId), reused on every retry. */
  clientEventId?: string;
  /** When the action happened on the phone (ISO), sent as deviceTimestamp / scannedAt. */
  deviceTimestamp?: string;
  /** 'failed' items are no longer retried automatically; the crew chooses Retry or Discard. */
  status?: 'pending' | 'failed';
  /** Last server message for a failed send, shown to the crew. */
  lastError?: string;
}

/** After this many server rejections an item stops retrying automatically. */
export const MAX_SYNC_RETRIES = 3;

/** Cached expected barcodes for one stop, snapshotted while online so a scan
 * made offline can be checked against the manifest locally before it ever
 * reaches the server (which makes the final counted/pending_confirmation call). */
export interface CachedManifest {
  stopId: string;
  barcodes: string[];
  cachedAt: string;
}

/** The signed-in user's id, used to scope the queue and the trip/attendance caches. */
export function currentOfflineUserId(): string | null {
  try {
    return getCachedUser()?.id || null;
  } catch {
    return null;
  }
}

export interface CachedCrewTrip {
  /** `current:<userId>` — one cached trip per signed-in user. */
  id: string;
  trip: Record<string, unknown> | null;
  cachedAt: string;
}

export interface CachedAttendance {
  id: string;
  status: {
    clockedIn: boolean;
    hubGeofenceMatch: boolean | null;
    hubDistanceM?: number | null;
    locationStatus?: string | null;
    recordedAt?: string | null;
  };
  cachedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(MANIFEST_STORE)) {
        db.createObjectStore(MANIFEST_STORE, { keyPath: 'stopId' });
      }
      if (!db.objectStoreNames.contains(TRIP_STORE)) {
        db.createObjectStore(TRIP_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(ATTENDANCE_STORE)) {
        db.createObjectStore(ATTENDANCE_STORE, { keyPath: 'id' });
      }
    };
  });
}

/** Snapshot the current stop's expected barcodes while online so offline scans
 * later on the same stop can be validated locally (see `isOnCachedManifest`). */
export async function cacheManifestBarcodes(stopId: string, barcodes: string[]): Promise<void> {
  if (!stopId) return;
  const db = await openDb();
  const tx = db.transaction(MANIFEST_STORE, 'readwrite');
  const record: CachedManifest = { stopId, barcodes, cachedAt: new Date().toISOString() };
  tx.objectStore(MANIFEST_STORE).put(record);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function getCachedManifest(stopId: string): Promise<CachedManifest | null> {
  if (!stopId) return null;
  const db = await openDb();
  const tx = db.transaction(MANIFEST_STORE, 'readonly');
  const request = tx.objectStore(MANIFEST_STORE).get(stopId);
  const result = await new Promise<CachedManifest | undefined>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return result ?? null;
}

/** Local, best-effort validation only — the server always makes the
 * authoritative counted/pending_confirmation/over decision once synced.
 * Returns null when the stop's manifest was never cached (e.g. never opened
 * while online), in which case no local claim should be made either way. */
export async function isOnCachedManifest(stopId: string, barcode: string): Promise<boolean | null> {
  const cached = await getCachedManifest(stopId);
  if (!cached) return null;
  return cached.barcodes.includes(barcode);
}

/** Persist last known crew trip so scan/POD/events work after auth hydrate offline. */
export async function cacheCrewTrip(trip: Record<string, unknown> | null): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(TRIP_STORE, 'readwrite');
  const record: CachedCrewTrip = {
    id: userCacheKey(currentOfflineUserId()),
    trip,
    cachedAt: new Date().toISOString(),
  };
  tx.objectStore(TRIP_STORE).put(record);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function getCachedCrewTrip<T = Record<string, unknown>>(): Promise<T | null> {
  const db = await openDb();
  const tx = db.transaction(TRIP_STORE, 'readonly');
  // Never fall back to another user's cached trip (or the old unscoped 'current' record).
  const request = tx.objectStore(TRIP_STORE).get(userCacheKey(currentOfflineUserId()));
  const result = await new Promise<CachedCrewTrip | undefined>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return (result?.trip as T | null | undefined) ?? null;
}

export async function cacheAttendanceStatus(status: CachedAttendance['status']): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(ATTENDANCE_STORE, 'readwrite');
  const record: CachedAttendance = {
    id: userCacheKey(currentOfflineUserId()),
    status,
    cachedAt: new Date().toISOString(),
  };
  tx.objectStore(ATTENDANCE_STORE).put(record);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function getCachedAttendanceStatus(): Promise<CachedAttendance['status'] | null> {
  const db = await openDb();
  const tx = db.transaction(ATTENDANCE_STORE, 'readonly');
  const request = tx.objectStore(ATTENDANCE_STORE).get(userCacheKey(currentOfflineUserId()));
  const result = await new Promise<CachedAttendance | undefined>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return result?.status ?? null;
}

export function notifyOfflineQueueChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_CHANGED));
  }
}

/**
 * Saves an action to send later. Pass the `clientEventId`/`deviceTimestamp` used for the
 * failed online attempt (newCrewActionMeta) so a replay of a request that did reach the
 * server is recognised as the same action; otherwise fresh ones are created here.
 */
export async function queueEvent(
  event: Omit<QueuedEvent, 'id' | 'createdAt' | 'retries' | 'userId'>,
): Promise<string> {
  const db = await openDb();
  const id = crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const record: QueuedEvent = {
    id,
    ...event,
    clientEventId: event.clientEventId ?? id,
    deviceTimestamp: event.deviceTimestamp ?? createdAt,
    userId: currentOfflineUserId() ?? undefined,
    createdAt,
    retries: 0,
  };
  const tx = db.transaction(QUEUE_STORE, 'readwrite');
  tx.objectStore(QUEUE_STORE).add(record);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  notifyOfflineQueueChanged();
  return id;
}

export async function getQueuedEvents(): Promise<QueuedEvent[]> {
  const db = await openDb();
  const tx = db.transaction(QUEUE_STORE, 'readonly');
  const request = tx.objectStore(QUEUE_STORE).getAll();
  const events = await new Promise<QueuedEvent[]>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result ?? []);
    request.onerror = () => reject(request.error);
  });
  db.close();
  // Only the signed-in user's items; other users' items wait for them to sign in again.
  return itemsForUser(events, currentOfflineUserId()).sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  );
}

function isFailed(event: QueuedEvent): boolean {
  return event.status === 'failed';
}

/** Items still waiting to send (excludes ones that failed and need a decision). */
export async function getQueueCount(): Promise<number> {
  const events = await getQueuedEvents();
  return events.filter((e) => !isFailed(e)).length;
}

export async function getFailedEvents(): Promise<QueuedEvent[]> {
  const events = await getQueuedEvents();
  return events.filter(isFailed);
}

async function putQueuedEvent(record: QueuedEvent): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(QUEUE_STORE, 'readwrite');
  tx.objectStore(QUEUE_STORE).put(record);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  notifyOfflineQueueChanged();
}

export async function removeQueuedEvent(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(QUEUE_STORE, 'readwrite');
  tx.objectStore(QUEUE_STORE).delete(id);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  notifyOfflineQueueChanged();
}

/** Put failed items back in the send queue (crew tapped Retry). */
export async function retryFailedEvents(): Promise<void> {
  const failed = await getFailedEvents();
  for (const event of failed) {
    await putQueuedEvent({ ...event, status: 'pending', retries: 0, lastError: undefined });
  }
}

/** Drop failed items for good (crew tapped Discard). */
export async function discardFailedEvents(): Promise<void> {
  const failed = await getFailedEvents();
  for (const event of failed) {
    await removeQueuedEvent(event.id);
  }
}

type SyncErrorKind = 'transient' | 'rejected';

/** Network drops / expired sign-in are not the item's fault: stop and try again later.
 * Any other error counts against the item. */
function classifySyncError(err: unknown): { kind: SyncErrorKind; status?: number; message: string } {
  const e = err as { status?: number; message?: string } | undefined;
  const status = typeof e?.status === 'number' ? e.status : undefined;
  const message = e?.message ?? 'Unknown error';
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline || status === 0 || status === 401 || status === 408 || status === 429) {
    return { kind: 'transient', status, message };
  }
  // Account must set a new password first (403 PASSWORD_CHANGE_REQUIRED): not the item's fault.
  if (status === 403 && /set a new password/i.test(message)) {
    return { kind: 'transient', status, message };
  }
  if (status == null && /network|timed out|timeout|failed to fetch|load failed|aborted/i.test(message)) {
    return { kind: 'transient', status, message };
  }
  return { kind: 'rejected', status, message };
}

export async function syncQueue(
  processor: (event: QueuedEvent) => Promise<void>,
): Promise<{ synced: number; failed: number }> {
  const events = (await getQueuedEvents()).filter((e) => !isFailed(e));
  let synced = 0;
  let failed = 0;

  for (const event of events) {
    try {
      await processor(event);
      await removeQueuedEvent(event.id);
      synced++;
    } catch (err) {
      const classified = classifySyncError(err);
      if (classified.kind === 'transient') {
        // Signal dropped again (or sign-in expired) — keep everything pending and stop.
        break;
      }
      failed++;
      const retries = (event.retries ?? 0) + 1;
      const clientError =
        classified.status != null && classified.status >= 400 && classified.status < 500;
      await putQueuedEvent({
        ...event,
        retries,
        lastError: classified.message,
        status: clientError || retries >= MAX_SYNC_RETRIES ? 'failed' : 'pending',
      });
    }
  }

  return { synced, failed };
}
